import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { toastify } from '../../../../../../utils/toast';
import { useFacilityStore } from '../../../../../../app/z-store/store';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { analog_base_url } from "../../../../../../utils/urls";
import AnalogMeterImportModal from '../../AnalogMeterImportModal';

const AnalogMetersManagementTab = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [meters, setMeters] = useState([]);
    const [rows, setRows] = useState(10);
    const [loading, setLoading] = useState(true);
    const [showImport, setShowImport] = useState(false);
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);
    
    // Pagination options
    const rowsPerPageOptions = [10, 20, 50, 100];

    useEffect(() => {
        if (selectedFacilityId) {
            fetchMeters();
        }
    }, [selectedFacilityId]);

    const fetchMeters = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${analog_base_url}/${selectedFacilityId}`,
                'GET'
            );

            if (response.success) {
                const metersData = response.data?.meters || [];
                setMeters(metersData);
            } else {
                toastify('Failed to fetch analog meters', 'error');
            }
        } catch (error) {
            toastify('Error loading meters', 'error');
        } finally {
            setLoading(false);
        }
    };

    const statusBodyTemplate = (rowData) => {
        const statusColors = {
            'maintenance': 'bg-info text-light',
            'opened': 'bg-success text-light',
            'closed': 'bg-danger text-light',
            'faulty': 'bg-danger text-light'
        };
        return (
            <span className={`badge ${statusColors[rowData.status] || 'bg-secondary text-light'}`}>
                {rowData.status || 'unknown'}
            </span>
        );
    };

    const handleViewMeter = (meter) => {
        navigate('/facility/utility_management/meter_management/water_meters/ViewAnalogMeter', {
            state: { meterId: meter._id }
        });
    };

    const actionBodyTemplate = (rowData) => (
        <div>
            <Button
                icon="ti ti-eye"
                className="p-button-rounded p-button-eye p-mr-2"
                onClick={() => handleViewMeter(rowData)}
                tooltip="View Meter"
            />
        </div>
    );

    // Search filter
    const filteredMeters = meters.filter(meter => {
        if (!searchTerm) return true;

        const searchLower = searchTerm.toLowerCase();

        // Search in meter number
        const meterNumberMatch = meter.meterNumber?.toString().toLowerCase().includes(searchLower);

        // Search in unit name (from UnitInfo)
        const unitMatch = meter.UnitInfo?.name?.toString().toLowerCase().includes(searchLower);

        return meterNumberMatch || unitMatch;
    });

    // If no facility selected, show a prompt
    if (!selectedFacilityId) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    Please select a facility first
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-body">
                {/* Top Right Button */}
                <div style={{ float: 'right' }}>
                    <button className="btn btn-outline-primary" onClick={() => setShowImport(true)}>
                        Import Meters
                    </button>
                    &nbsp;&nbsp;
                    <button
                        className="btn btn-primary"
                        onClick={() =>
                            navigate('/facility/utility_management/meter_management/water_meters/addAnalogMeter')
                        }
                    >
                        Add New Meter
                    </button>
                </div>

                {/* Search Input */}
                <div className="col-md-3 col-xs-12 mb-3">
                    <input
                        className="form-control"
                        placeholder="Search meters..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Data Table */}
                <div className="dt-responsive table-responsive">
                    <DataTable
                        value={filteredMeters}
                        emptyMessage="No analog meters found."
                        sortMode="multiple"
                        paginator
                        rows={rows}
                        rowsPerPageOptions={rowsPerPageOptions}
                        stripedRows
                        loading={loading}
                        tableStyle={{ minWidth: '50rem' }}
                    >
                        <Column field="meterNumber" header="Meter S/N" sortable />
                        <Column
                            field="UnitInfo"
                            header="Unit"
                            sortable
                            body={(rowData) =>
                                rowData.UnitInfo
                                    ? rowData.UnitInfo.name || rowData.UnitInfo.unitNumber
                                    : 'N/A'
                            }
                        />
                        <Column field="status" header="Status" body={statusBodyTemplate} sortable />
                        <Column
                            header="Actions"
                            body={actionBodyTemplate}
                            style={{ width: '10rem' }}
                        />
                    </DataTable>
                </div>

                {/* Confirm Dialog */}
                <ConfirmDialog />

                {showImport && (
                    <AnalogMeterImportModal
                        facilityId={selectedFacilityId}
                        importUrl={`${analog_base_url}/import_meters`}
                        onHide={() => setShowImport(false)}
                        onImported={fetchMeters}
                    />
                )}
            </div>
        </div>
    );
};

export default AnalogMetersManagementTab;